import { useState, useEffect, useCallback, useRef } from 'react';
import { snowflakeService, SnowflakeResponse } from '../services/snowflakeService';

export interface FileInfo {
  file_name: string;
  load_status: string;
  created_at: string;
}

export interface KeywordReadinessData {
  status: 'ready' | 'not_ready' | 'checking' | 'error';
  keywordCount?: number;
  fileCount?: number;
  files?: FileInfo[];
  lastUpdated?: string;
  message?: string;
}

export interface UseKeywordReadinessOptions {
  clientId?: string;
  workflowId?: string;
  enabled?: boolean;
  pollingInterval?: number; // in milliseconds
  maxRetries?: number;
}

export const useKeywordReadiness = (options: UseKeywordReadinessOptions = {}) => {
  const {
    clientId,
    workflowId,
    enabled = true,
    pollingInterval = 5000, // 5 seconds
    maxRetries = 10
  } = options;

  const [data, setData] = useState<KeywordReadinessData>({
    status: 'checking'
  });
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);

  const checkReadiness = useCallback(async (): Promise<KeywordReadinessData> => {
    if (!clientId) {
      return {
        status: 'error',
        message: 'Client ID is required'
      };
    }

    if (!workflowId) {
      return {
        status: 'error',
        message: 'Workflow ID is required'
      };
    }
    
    try {
      const response: SnowflakeResponse = await snowflakeService.checkKeywordReadiness(clientId, workflowId);
      
      if (response.success && response.data) {
        // Reset retry count on successful response
        retryCountRef.current = 0;
        
        // Transform the response data to match our interface
        // IMPORTANT: Don't mark as 'ready' if keyword count is 0, even if backend says ready
        const isActuallyReady = response.data.ready && (response.data.keyword_count || 0) > 0;
        
        const transformedData: KeywordReadinessData = {
          status: isActuallyReady ? 'ready' : 'not_ready',
          keywordCount: response.data.keyword_count,
          fileCount: response.data.file_count,
          files: response.data.files,
          lastUpdated: new Date().toISOString(),
          message: response.data.message || response.message
        };
        
        return transformedData;
      } else {
        // Handle API error response
        return {
          status: 'error',
          message: response.error || 'Failed to check keyword readiness'
        };
      }
    } catch (error: any) {
      // Handle network or other errors
      return {
        status: 'error',
        message: error.message || 'Network error while checking keyword readiness'
      };
    }
  }, [clientId, workflowId]);

  const startPolling = useCallback(async () => {
    if (!enabled || !clientId || !workflowId) {
      return;
    }

    // Stop any existing polling first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    setIsPolling(true);
    setError(null);
    
    const poll = async () => {
      try {
        const result = await checkReadiness();
        setData(result);
        
        // Reset error counter on successful API call
        retryCountRef.current = 0;
        
        // If ready or error, stop polling
        if (result.status === 'ready' || result.status === 'error') {
          setIsPolling(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return;
        }
        
      } catch (error: any) {
        // Increment error counter for actual network/API errors
        retryCountRef.current++;
        
        if (retryCountRef.current >= maxRetries) {
          setError(error.message || 'Error during polling');
          setData(prev => ({
            ...prev,
            status: 'error',
            message: `Failed after ${maxRetries} network errors. Please try again later.`
          }));
          setIsPolling(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
        // Otherwise continue polling - might be a temporary network issue
      }
    };

    // Initial check
    const initialResult = await checkReadiness();
    setData(initialResult);
    
    // Only continue polling if the initial status is not ready and not error
    if (initialResult.status !== 'ready' && initialResult.status !== 'error') {
      pollingIntervalRef.current = setInterval(poll, pollingInterval);
    } else {
      // If already ready or error, stop polling
      setIsPolling(false);
    }
  }, [enabled, clientId, workflowId, checkReadiness, pollingInterval, maxRetries]);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const refresh = useCallback(async () => {
    stopPolling();
    retryCountRef.current = 0;
    setError(null);
    
    // Do a one-time check - don't automatically restart polling
    const result = await checkReadiness();
    setData(result);
    
    // Only start polling if the result shows we need to continue polling
    if (result.status === 'not_ready') {
      startPolling();
    }
  }, [stopPolling, checkReadiness, startPolling]);

  // DISABLED: Auto-start polling - now controlled manually by components
  // This was causing infinite loops when components manually control polling
  // useEffect(() => {
  //   if (enabled && clientId && workflowId) {
  //     startPolling();
  //   } else {
  //     stopPolling();
  //   }
  //   return () => {
  //     stopPolling();
  //   };
  // }, [enabled, clientId, workflowId, startPolling, stopPolling]);

  // Stop polling when page becomes hidden (but don't auto-restart)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [stopPolling]);

  // Cleanup: Stop polling on component unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    data,
    isPolling,
    error,
    startPolling,
    stopPolling,
    refresh
  };
}; 